/**
 * ============================================================================
 * Docs Content - Sistema de conteúdo multilíngue para documentação
 * ============================================================================
 *
 * Este módulo gerencia os componentes de conteúdo por idioma.
 * Cada página de documentação tem uma versão para cada idioma suportado.
 *
 * Para adicionar uma nova página:
 * 1. Crie o componente em cada pasta de idioma (pt-BR, pt-PT, es)
 * 2. Exporte no index.ts de cada idioma
 * 3. Adicione no contentByLanguage abaixo
 */

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '@/i18n/config';

// Importações lazy para otimização de bundle
const PtBRContent = {
  IntroductionPage: lazy(() => import('./pt-BR/IntroductionPage').then(m => ({ default: m.IntroductionPage }))),
  ConceitosFundamentaisPage: lazy(() => import('./pt-BR/ConceitosFundamentaisPage').then(m => ({ default: m.ConceitosFundamentaisPage }))),
  VisaoGeralPage: lazy(() => import('./pt-BR/VisaoGeralPage').then(m => ({ default: m.VisaoGeralPage }))),
  ProjetosPage: lazy(() => import('./pt-BR/ProjetosPage').then(m => ({ default: m.ProjetosPage }))),
  AtasDeReuniaoPage: lazy(() => import('./pt-BR/AtasDeReuniaoPage').then(m => ({ default: m.AtasDeReuniaoPage }))),
  TopicosEItensPage: lazy(() => import('./pt-BR/TopicosEItensPage').then(m => ({ default: m.TopicosEItensPage }))),
  DesenvolvimentosPage: lazy(() => import('./pt-BR/DesenvolvimentosPage').then(m => ({ default: m.DesenvolvimentosPage }))),
  ParticipantesPage: lazy(() => import('./pt-BR/ParticipantesPage').then(m => ({ default: m.ParticipantesPage }))),
  ConvidarEquipePage: lazy(() => import('./pt-BR/ConvidarEquipePage').then(m => ({ default: m.ConvidarEquipePage }))),
  MapaDeAtasPage: lazy(() => import('./pt-BR/MapaDeAtasPage').then(m => ({ default: m.MapaDeAtasPage }))),
  PreviewExportacaoPage: lazy(() => import('./pt-BR/PreviewExportacaoPage').then(m => ({ default: m.PreviewExportacaoPage }))),
  OrganizacaoColaboracaoPage: lazy(() => import('./pt-BR/OrganizacaoColaboracaoPage').then(m => ({ default: m.OrganizacaoColaboracaoPage }))),
  PersonalizacaoPage: lazy(() => import('./pt-BR/PersonalizacaoPage').then(m => ({ default: m.PersonalizacaoPage }))),
  BoasPraticasAtasPage: lazy(() => import('./pt-BR/BoasPraticasAtasPage').then(m => ({ default: m.BoasPraticasAtasPage }))),
  NovidadesPage: lazy(() => import('./pt-BR/NovidadesPage').then(m => ({ default: m.NovidadesPage }))),
};

const PtPTContent = {
  IntroductionPage: lazy(() => import('./pt-PT/IntroductionPage').then(m => ({ default: m.IntroductionPage }))),
  ConceitosFundamentaisPage: lazy(() => import('./pt-PT/ConceitosFundamentaisPage').then(m => ({ default: m.ConceitosFundamentaisPage }))),
  VisaoGeralPage: lazy(() => import('./pt-PT/VisaoGeralPage').then(m => ({ default: m.VisaoGeralPage }))),
  ProjetosPage: lazy(() => import('./pt-PT/ProjetosPage').then(m => ({ default: m.ProjetosPage }))),
  AtasDeReuniaoPage: lazy(() => import('./pt-PT/AtasDeReuniaoPage').then(m => ({ default: m.AtasDeReuniaoPage }))),
  TopicosEItensPage: lazy(() => import('./pt-PT/TopicosEItensPage').then(m => ({ default: m.TopicosEItensPage }))),
  DesenvolvimentosPage: lazy(() => import('./pt-PT/DesenvolvimentosPage').then(m => ({ default: m.DesenvolvimentosPage }))),
  ParticipantesPage: lazy(() => import('./pt-PT/ParticipantesPage').then(m => ({ default: m.ParticipantesPage }))),
  ConvidarEquipePage: lazy(() => import('./pt-PT/ConvidarEquipePage').then(m => ({ default: m.ConvidarEquipePage }))),
  MapaDeAtasPage: lazy(() => import('./pt-PT/MapaDeAtasPage').then(m => ({ default: m.MapaDeAtasPage }))),
  PreviewExportacaoPage: lazy(() => import('./pt-PT/PreviewExportacaoPage').then(m => ({ default: m.PreviewExportacaoPage }))),
  OrganizacaoColaboracaoPage: lazy(() => import('./pt-PT/OrganizacaoColaboracaoPage').then(m => ({ default: m.OrganizacaoColaboracaoPage }))),
  PersonalizacaoPage: lazy(() => import('./pt-PT/PersonalizacaoPage').then(m => ({ default: m.PersonalizacaoPage }))),
  BoasPraticasAtasPage: lazy(() => import('./pt-PT/BoasPraticasAtasPage').then(m => ({ default: m.BoasPraticasAtasPage }))),
  NovidadesPage: lazy(() => import('./pt-PT/NovidadesPage').then(m => ({ default: m.NovidadesPage }))),
};

const EsContent = {
  IntroductionPage: lazy(() => import('./es/IntroductionPage').then(m => ({ default: m.IntroductionPage }))),
  ConceitosFundamentaisPage: lazy(() => import('./es/ConceitosFundamentaisPage').then(m => ({ default: m.ConceitosFundamentaisPage }))),
  VisaoGeralPage: lazy(() => import('./es/VisaoGeralPage').then(m => ({ default: m.VisaoGeralPage }))),
  ProjetosPage: lazy(() => import('./es/ProjetosPage').then(m => ({ default: m.ProjetosPage }))),
  AtasDeReuniaoPage: lazy(() => import('./es/AtasDeReuniaoPage').then(m => ({ default: m.AtasDeReuniaoPage }))),
  TopicosEItensPage: lazy(() => import('./es/TopicosEItensPage').then(m => ({ default: m.TopicosEItensPage }))),
  DesenvolvimentosPage: lazy(() => import('./es/DesenvolvimentosPage').then(m => ({ default: m.DesenvolvimentosPage }))),
  ParticipantesPage: lazy(() => import('./es/ParticipantesPage').then(m => ({ default: m.ParticipantesPage }))),
  ConvidarEquipePage: lazy(() => import('./es/ConvidarEquipePage').then(m => ({ default: m.ConvidarEquipePage }))),
  MapaDeAtasPage: lazy(() => import('./es/MapaDeAtasPage').then(m => ({ default: m.MapaDeAtasPage }))),
  PreviewExportacaoPage: lazy(() => import('./es/PreviewExportacaoPage').then(m => ({ default: m.PreviewExportacaoPage }))),
  OrganizacaoColaboracaoPage: lazy(() => import('./es/OrganizacaoColaboracaoPage').then(m => ({ default: m.OrganizacaoColaboracaoPage }))),
  PersonalizacaoPage: lazy(() => import('./es/PersonalizacaoPage').then(m => ({ default: m.PersonalizacaoPage }))),
  BoasPraticasAtasPage: lazy(() => import('./es/BoasPraticasAtasPage').then(m => ({ default: m.BoasPraticasAtasPage }))),
  NovidadesPage: lazy(() => import('./es/NovidadesPage').then(m => ({ default: m.NovidadesPage }))),
};

const EnContent = {
  IntroductionPage: lazy(() => import('./en/IntroductionPage').then(m => ({ default: m.IntroductionPage }))),
  ConceitosFundamentaisPage: lazy(() => import('./en/ConceitosFundamentaisPage').then(m => ({ default: m.ConceitosFundamentaisPage }))),
  VisaoGeralPage: lazy(() => import('./en/VisaoGeralPage').then(m => ({ default: m.VisaoGeralPage }))),
  ProjetosPage: lazy(() => import('./en/ProjetosPage').then(m => ({ default: m.ProjetosPage }))),
  AtasDeReuniaoPage: lazy(() => import('./en/AtasDeReuniaoPage').then(m => ({ default: m.AtasDeReuniaoPage }))),
  TopicosEItensPage: lazy(() => import('./en/TopicosEItensPage').then(m => ({ default: m.TopicosEItensPage }))),
  DesenvolvimentosPage: lazy(() => import('./en/DesenvolvimentosPage').then(m => ({ default: m.DesenvolvimentosPage }))),
  ParticipantesPage: lazy(() => import('./en/ParticipantesPage').then(m => ({ default: m.ParticipantesPage }))),
  ConvidarEquipePage: lazy(() => import('./en/ConvidarEquipePage').then(m => ({ default: m.ConvidarEquipePage }))),
  MapaDeAtasPage: lazy(() => import('./en/MapaDeAtasPage').then(m => ({ default: m.MapaDeAtasPage }))),
  PreviewExportacaoPage: lazy(() => import('./en/PreviewExportacaoPage').then(m => ({ default: m.PreviewExportacaoPage }))),
  OrganizacaoColaboracaoPage: lazy(() => import('./en/OrganizacaoColaboracaoPage').then(m => ({ default: m.OrganizacaoColaboracaoPage }))),
  PersonalizacaoPage: lazy(() => import('./en/PersonalizacaoPage').then(m => ({ default: m.PersonalizacaoPage }))),
  BoasPraticasAtasPage: lazy(() => import('./en/BoasPraticasAtasPage').then(m => ({ default: m.BoasPraticasAtasPage }))),
  NovidadesPage: lazy(() => import('./en/NovidadesPage').then(m => ({ default: m.NovidadesPage }))),
};

// Mapeamento de conteúdo por idioma
const contentByLanguage: Record<SupportedLanguage, typeof PtBRContent> = {
  'pt-BR': PtBRContent,
  'pt-PT': PtPTContent,
  'es': EsContent,
  'en': EnContent,
};

// Tipo para os nomes das páginas disponíveis
export type DocsPageName = keyof typeof PtBRContent;

// Loading component para Suspense
function PageLoading() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

/**
 * Hook para obter o componente de conteúdo correto baseado no idioma atual
 */
export function useDocsContent() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguage;

  // Fallback para pt-BR se o idioma não for suportado
  const language = contentByLanguage[currentLanguage] ? currentLanguage : 'pt-BR';

  return contentByLanguage[language];
}

/**
 * Componente wrapper que renderiza o conteúdo correto baseado no idioma
 */
interface LocalizedPageProps {
  page: DocsPageName;
}

export function LocalizedPage({ page }: LocalizedPageProps) {
  const content = useDocsContent();
  const PageComponent = content[page];

  if (!PageComponent) {
    return <div>Página não encontrada</div>;
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <PageComponent />
    </Suspense>
  );
}

// Re-export dos componentes individuais para uso direto se necessário
export { PtBRContent, PtPTContent, EsContent, EnContent };
